/** Light, dark or follow the system (default). The choice is saved per browser. */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Choice = 'light' | 'dark' | 'system';

interface ThemeState {
  choice: Choice;
  resolved: 'light' | 'dark';
  toggle: () => void;
  setChoice: (choice: Choice) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);
export const STORAGE_KEY = 'eros.theme';

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [choice, setChoice] = useState<Choice>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : 'system';
    } catch {
      return 'system';
    }
  });
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent): void => setSystemDark(event.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const resolved: 'light' | 'dark' = choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.classList.toggle('light', resolved === 'light');
    document.querySelector('meta[name="theme-color"]:not([media])')?.setAttribute('content', resolved === 'dark' ? '#0E1412' : '#0E5A46');
    try {
      if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // Private mode: the choice lasts for this visit only.
    }
  }, [choice, resolved]);

  const value = useMemo<ThemeState>(
    () => ({
      choice,
      resolved,
      setChoice,
      toggle: () => setChoice(choice === 'light' ? 'dark' : choice === 'dark' ? 'system' : 'light'),
    }),
    [choice, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
